'use client';

import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@commenda/kakashi';
import { MONTHS } from '@commenda/utils/form-builder';
import { useContext, useEffect, useMemo, useState } from 'react';
import { FormContext } from '../../hooks/FormContext';
import { isRuleActive } from '../../utils/validation';
import { parseFiscalYearValue } from '../../utils/fiscal-year';
import { LabelWithGuidanceTooltip } from './LabelWithGuidanceTooltip';

/**
 * FiscalYearInput Component
 *
 * A specialized input for fiscal year end dates that displays two side-by-side
 * dropdowns for month and day selection. The value is stored as a string in
 * "MM-DD" format (e.g., "12-31" for December 31).
 */

// Days in each month (non-leap year)
const DAYS_IN_MONTH: Record<string, number> = {
  '01': 31,
  '02': 28,
  '03': 31,
  '04': 30,
  '05': 31,
  '06': 30,
  '07': 31,
  '08': 31,
  '09': 30,
  '10': 31,
  '11': 30,
  '12': 31,
};

export const FiscalYearInputComponent = ({
  fieldId,
  label,
  description,
  placeholder,
  guidance,
  validation,
  props = {},
}: {
  fieldId: string;
  label?: string;
  description?: string;
  placeholder?: string;
  guidance?: { text?: string };
  validation?: {
    rules?: {
      required?: { enabled?: boolean } | boolean;
    };
  };
  props?: Record<string, unknown>;
}) => {
  const {
    formData,
    updateField,
    errors,
    validateField,
    isFieldVisible,
    isFieldDisabled,
  } = useContext(FormContext);
  const rawValue = formData[fieldId];
  const disabled = (props.disabled as boolean) || isFieldDisabled(fieldId);
  const isRequired = isRuleActive(validation?.rules?.required, isFieldVisible);

  // Parse stored value into month and day (handles prefills / external updates)
  const { month: storedMonth, day: storedDay } = useMemo(
    () => parseFiscalYearValue(rawValue),
    [rawValue]
  );

  // Local state tracks intermediate selections (month picked but day not yet chosen)
  const [localMonth, setLocalMonth] = useState(storedMonth);
  const [localDay, setLocalDay] = useState(storedDay);

  // Sync local state when the stored value changes externally (prefill, reset)
  useEffect(() => {
    setLocalMonth(storedMonth);
    setLocalDay(storedDay);
  }, [storedMonth, storedDay]);

  // Generate day options based on locally selected month
  const dayOptions = useMemo(() => {
    const maxDays = localMonth ? DAYS_IN_MONTH[localMonth] || 31 : 31;
    return Array.from({ length: maxDays }, (_, i) => {
      const dayNum = (i + 1).toString().padStart(2, '0');
      return { value: dayNum, label: (i + 1).toString() };
    });
  }, [localMonth]);

  const handleMonthChange = (newMonth: string) => {
    const maxDays = DAYS_IN_MONTH[newMonth] || 31;
    const currentDay = parseInt(localDay, 10);
    // Keep existing day if valid for the new month, otherwise clear it
    const newDay =
      currentDay > 0 && !Number.isNaN(currentDay) && currentDay <= maxDays
        ? localDay
        : '';

    setLocalMonth(newMonth);
    setLocalDay(newDay);

    // Only write to formData when we have a complete date
    const newValue = newDay ? `${newMonth}-${newDay}` : '';
    updateField(fieldId, newValue);
    if (validation?.rules) {
      validateField(fieldId, newValue, validation.rules);
    }
  };

  const handleDayChange = (newDay: string) => {
    setLocalDay(newDay);
    const newValue = localMonth ? `${localMonth}-${newDay}` : '';
    updateField(fieldId, newValue);
    if (validation?.rules) {
      validateField(fieldId, newValue, validation.rules);
    }
  };

  return (
    <div className="mb-4" data-field-id={fieldId}>
      {(label || description) && (
        <LabelWithGuidanceTooltip
          label={label}
          guidance={guidance?.text}
          isRequired={isRequired}
          description={description}
        />
      )}
      <div className="flex gap-3">
        <div className="flex-1">
          <Select
            value={localMonth}
            onValueChange={handleMonthChange}
            disabled={disabled}
          >
            <SelectTrigger error={errors[fieldId]}>
              <SelectValue placeholder={placeholder || 'Month'} />
            </SelectTrigger>
            <SelectContent>
              {MONTHS.map((m) => (
                <SelectItem key={m.value} value={m.value}>
                  {m.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="w-24">
          <Select
            value={localDay}
            onValueChange={handleDayChange}
            disabled={disabled || !localMonth}
          >
            <SelectTrigger error={errors[fieldId]}>
              <SelectValue placeholder="Day" />
            </SelectTrigger>
            <SelectContent>
              {dayOptions.map((d) => (
                <SelectItem key={d.value} value={d.value}>
                  {d.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>
      {errors[fieldId] && (
        <p className="mt-1 text-sm text-text-error">{errors[fieldId]}</p>
      )}
    </div>
  );
};
